import { usePopup } from '@contexts/popupContext';
import { Application } from '@core/Application';
import { Organization } from '@models/Organization';
import styles from '@styles/components/Configuracion/Usuarios/ListadoRoles.module.css';
import { useCallback, useEffect, useState } from 'react';

import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import { ReactComponent as CloseIcon } from '@assets/SVGs/GeneralUse/XIcon.svg';
import { VirtualizedList } from '@components/General/VirtualizedList';
import { HollowedButton } from '@components/General/Button';
import { BorderColorOutlined } from '@mui/icons-material';
import { QSwitch } from '@shared/designSystem/QSwitch';
import { TextInput } from '@components/General/TextInput';
import { Permissions, Role } from '@models/Permissions';
import { useNavigate } from 'react-router-dom';
import ConfirmationPopup from '@components/General/ConfirmationPopup';
import Loading from '@components/General/Loading';
import ResultPopup from '@components/General/ResultPopup';


const AddRolePopup: React.FC<{onConfirm: (roleName: string) => any}> = ({onConfirm}) => {
	const {showPopup, hidePopup} = usePopup();
	const [roleName, setRoleName] = useState<string>('');

	return (
		<div className={styles['edit-permissions-popup']}>
			<div className={styles['popup-header']}>
				<p>Agregar rol</p>
				<div className={styles['close-button']} onClick={() => {
					hidePopup();
				}}>
					<CloseIcon />
				</div>
			</div>
			<div className={styles['popup-subheader']}>
				<TextInput
					label="Rol"
					placeholder={"Ingresa el nombre completo".localized()}
					onChange={(e) => {
						setRoleName(e);
					}}
					value={roleName}
					error={''}
				/>
			</div>
			<div className={styles['popup-footer']}>
				<div className={styles['button-container']}>
					{/* <HollowedButton text="Cancelar" onClick={() => {
						// close popup
						hidePopup();
					}}/> */}
				</div>
				<div className={styles['button-container']}>
					<HollowedButton text="Guardar" onClick={() => {
						onConfirm(roleName);
					}}/>
				</div>
			</div>
		</div>	
	)
}


const ListadoRoles: React.FC<{org: Organization}> = ({org}) => {
	const { showPopup } = usePopup();
	const navigate = useNavigate();
	const [roles, setRoles] = useState<Role[]>([]);
	const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        fetchRoles();
    }, [org]);

	const sortRoles = (roles: Role[]) => {
		return roles.sort((a, b) => {
			const aPermissionCount = Object.keys(a.permissions).length;
			const bPermissionCount = Object.keys(b.permissions).length;
			return bPermissionCount - aPermissionCount; // Sort descending by permission count
		}).filter(role => role.id !== 'default-none');
	}

    const fetchRoles = async () => {
		setLoading(true)
        const service = Application.shared().coreDependencies.rolesService;
        try {
            const newRoles = await service.getRoles(org.id);
            setRoles(sortRoles(newRoles));
        } catch (error) {
            console.error("Error fetching roles:", error);
        } finally {
			setLoading(false)
		}
    }

	const onEditClick = useCallback((role: Role) => {
		navigate('/configuracion/usuarios/rol', {state: {role}});
	}, [showPopup]);

	
	const onDisableClick = async (roleId: string) => {
		const role = roles.find((u) => u.id === roleId);
		if (role) {
			showPopup(() => (
				<ConfirmationPopup
					title={'Deshabilitar rol'.localized()}
					message={`¿Estás seguro de que deseas deshabilitar el rol ${role.name}?`.localized()}
					onConfirm={async () => {
                        const service = Application.shared().coreDependencies.rolesService;
                        try {
                            await service.deleteRole(org.id, roleId);
                        } catch (error) {
                            console.error("Error deactivating user:", error);
                            throw error;
                        }
                    }}
					onSuccess={async () => {
						setRoles((prevUsers) => prevUsers.filter((u) => u.id !== roleId));
					}}
					successMessage={`Rol ${role.name} deshabilitado con éxito.`.localized()}
					errorMessage={`No se pudo deshabilitar al rol ${role.name}. Por favor, inténtalo de nuevo.`.localized()}
				/>
			));
		}
	}


	const renderRow = (index: number, style: any) => {
		const role = roles[index];
		if (!role) return null;

		return (
			<div 
				key={role.id} 
				style={{...style, backgroundColor: index % 2 === 0 ? '#FAFAFA' : 'white' }}
				className={styles['role-list-row']}
			>
				<p>{role.name.localized()}</p>
				<div className={styles['permissions-container']}>
					<p>{Object.keys(role.permissions).map(p => role.permissions[p].id.localized()).join(', ')}</p>
					{Object.keys(role.permissions).length === 0 && <p>Sin permisos asignados</p>}
				</div>
				<div>
					<div
						className={styles['svg-button']}
						onClick={() => {
							onEditClick(role);
						}}>
						<BorderColorOutlined style={{ color: '#EC6C56' }} />
					</div>
					{role.id !== 'default-admin' && role.id !== 'default-none' && role.id !== 'default-reader' &&
						<div
							className={styles['svg-button']}
							style={{display: 'flex'}}
							onClick={() => {
								onDisableClick(role.id);
							}}>
							<DeleteOutlineOutlinedIcon style={{ color: '#EC6C56' }} />
						</div>
					}
				</div>
			</div>
		);
	}

	const onAddNewRoleClick = () => {
		showPopup(() => (
			<AddRolePopup 
				onConfirm={async (roleName) => {
					const service = Application.shared().coreDependencies.rolesService;
					try {
						await service.createRole(org.id, roleName);
						showPopup(() => (
							<ResultPopup result={`Creado rol ${roleName} correctamente!`} succesful={true}/>
						));
						await fetchRoles();
					} catch (error) {
						showPopup(() => (
							<ResultPopup result={`No se pudo crear el rol ${roleName}. Por favor, inténtalo de nuevo.`} succesful={false}/>
						));
						console.error("Error creating role:", error);
					}
				}}	
			/>
		), false);
	}


	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<div className={styles['header-title']}>
					<AdminPanelSettingsOutlinedIcon />
					<p>Roles y Permisos</p>
				</div>
			</div>
			<div className={styles.subheader}>
				<p className={styles.description}>Definir los roles y permisos que serán asignados a los usuarios del sistema:</p>
			</div>
			<div className={styles['role-list-container']}>
				<div className={`${styles['role-list-header']}`}>
					<span>Roles</span>
					<span>Permisos</span>
					<span></span>
				</div>

				{/* {roles.length === 0 && (
					<div className={styles['empty-container']}>
						<p>No hay roles disponibles.</p>
					</div>
				)} */}

				{loading ? <Loading/> :
				<div className={styles['role-list']}>
					<VirtualizedList
						itemCount={roles.length}
						isItemLoaded={(index) => index < roles.length}
						renderRow={renderRow}
						itemSize={75}
					/>
				</div>}
			</div>
			<div className={styles.footer}>
				<div className={styles['button-container']}>
					<HollowedButton text="+Nuevo rol" onClick={()=>{onAddNewRoleClick()}}/>
				</div>
			</div>
		</div>
	);
};

export default ListadoRoles;
